import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button as ShadcnButton } from '@/components/ui/button';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import {
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Select } from '@/components/ui/catalyst/select';
import { Z_INDEX_MODAL_HIGH } from '@/components/ui/zIndex';
import { useMatter } from '@/hooks/useMatter';
import {
  Blockers,
  CreateTreatmentRecordRequestChannelSchema,
  TreatmentRecordRequestMetadataSchema,
  TreatmentRecordRequestSchema,
} from '@/services/api/generated/treatmentRecordRequestsService';
import {
  useGetMatterQuery,
  usePatchMatterClientMutation,
  usePatchMatterMutation,
} from '@/services/api/mattersService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { MatterClient } from '@/services/types/matter-client-types';
import { MedicalProvider } from '@/services/types/treatment-types';

import {
  useSaveVoiceAICallFirm,
  useVoiceAICallFormData,
} from '../voice-ai/hooks/useVoiceAICall';
import { VoiceAICallForm } from '../voice-ai/VoiceAICallForm';

type NotNullOrUndefined<T> = T extends undefined | null ? never : T;

// Client info form types
type ClientInfoFormType = Partial<MatterClient> & {
  date_of_loss?: string;
};

// Validation schema
const clientInfoSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  date_of_birth: z.string().min(1, 'Date of birth is required'),
  date_of_loss: z.string().min(1, 'Date of loss is required'),
  ssn: z
    .string()
    .min(9, 'SSN must be at least 9 digits')
    .regex(/^\d{3}-?\d{2}-?\d{4}$/, 'Invalid SSN format'),
  gender: z.enum(['male', 'female', 'other'], {
    errorMap: () => ({ message: 'Please select a gender' }),
  }),
  address: z.object({
    postcode: z
      .string()
      .min(5, 'Zip code must be at least 5 digits')
      .regex(/^\d{5}(-\d{4})?$/, 'Invalid zip code format'),
  }),
});

type ClientInfoFormData = z.infer<typeof clientInfoSchema>;

export type BlockedState = Extract<
  NotNullOrUndefined<
    TreatmentRecordRequestMetadataSchema['automation_state_v2']
  >,
  { status: 'blocked' }
>;

const VoiceAICallModal = ({
  onClose,
  request_id,
  provider_id,
}: {
  onClose: () => void;
  request_id: string;
  provider_id: string;
}) => {
  const { matterId } = useMatter();
  const { values, handleChange } = useVoiceAICallFormData({
    requestId: request_id,
    matterId,
    providerId: provider_id,
  });

  const { saveVoiceAicallFirm } = useSaveVoiceAICallFirm({
    requestId: request_id,
    matterId,
    providerId: provider_id,
  });

  const handleSubmit = async () => {
    await saveVoiceAicallFirm(values);
    onClose();
  };

  return (
    <div className="space-y-6">
      <VoiceAICallForm values={values} onChange={handleChange} />
      <DialogActions>
        <Button color="zinc" onClick={onClose}>
          Cancel
        </Button>
        <Button color="indigo-steel" onClick={handleSubmit}>
          Save
        </Button>
      </DialogActions>
    </div>
  );
};

// Component for handling client information blocker
const ClientInfoModal = ({
  onClose,
  matterId,
  clientId,
}: {
  onClose: () => void;
  matterId: string;
  clientId?: string;
}) => {
  const { data: matter } = useGetMatterQuery(matterId);
  const [updateMatterClient, { isLoading: isUpdatingClient }] =
    usePatchMatterClientMutation();
  const [updateMatter, { isLoading: isUpdatingMatter }] =
    usePatchMatterMutation();

  const client = matter?.clients?.find((c) => c.id === clientId);

  const handleSubmit = async (data: ClientInfoFormType) => {
    if (!clientId) {
      toast.error('Client ID not found');
      return;
    }

    try {
      // Update client information
      await updateMatterClient({
        matter_id: matterId,
        matter_client_id: clientId,
        patch: data,
      }).unwrap();

      // Update matter date_of_loss if provided and not already set
      if (data.date_of_loss && !matter?.date_of_loss) {
        await updateMatter({
          matter_id: matterId,
          patch: { date_of_loss: data.date_of_loss },
        }).unwrap();
      }

      toast.success('Client information updated successfully');
      onClose();
    } catch (error) {
      toast.error('Failed to update client information');
    }
  };

  const isSubmitting = isUpdatingClient || isUpdatingMatter;

  // Form handling
  const {
    control,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid },
  } = useForm<ClientInfoFormData>({
    resolver: zodResolver(clientInfoSchema),
    mode: 'onChange',
    defaultValues: {
      first_name: client?.first_name || '',
      last_name: client?.last_name || '',
      date_of_birth: client?.date_of_birth || '',
      date_of_loss: matter?.date_of_loss || '',
      ssn: client?.ssn || '',
      gender: (client?.gender as 'male' | 'female' | 'other') || '',
      address: {
        postcode: client?.address?.postcode || '',
      },
    },
  });

  const onFormSubmit = async (data: ClientInfoFormData) => {
    await handleSubmit(data);
  };

  // Format SSN for display
  const formatSSN = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 5)
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5, 9)}`;
  };

  return (
    <form onSubmit={handleFormSubmit(onFormSubmit)}>
      <Fieldset>
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            {/* First Name */}
            <Field>
              <Label htmlFor="first_name">First Name</Label>
              <Controller
                name="first_name"
                control={control}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      id="first_name"
                      type="text"
                      placeholder="John"
                      invalid={!!errors.first_name}
                    />
                    {errors.first_name && (
                      <ErrorMessage>{errors.first_name.message}</ErrorMessage>
                    )}
                  </>
                )}
              />
            </Field>

            {/* Last Name */}
            <Field>
              <Label htmlFor="last_name">Last Name</Label>
              <Controller
                name="last_name"
                control={control}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      id="last_name"
                      type="text"
                      placeholder="Doe"
                      invalid={!!errors.last_name}
                    />
                    {errors.last_name && (
                      <ErrorMessage>{errors.last_name.message}</ErrorMessage>
                    )}
                  </>
                )}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Date of Birth */}
            <Field>
              <Label htmlFor="date_of_birth">Date of Birth</Label>
              <Controller
                name="date_of_birth"
                control={control}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      id="date_of_birth"
                      type="date"
                      invalid={!!errors.date_of_birth}
                    />
                    {errors.date_of_birth && (
                      <ErrorMessage>
                        {errors.date_of_birth.message}
                      </ErrorMessage>
                    )}
                  </>
                )}
              />
            </Field>

            {/* Date of Loss */}
            <Field>
              <Label htmlFor="date_of_loss">Date of Loss</Label>
              <Controller
                name="date_of_loss"
                control={control}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      id="date_of_loss"
                      type="date"
                      invalid={!!errors.date_of_loss}
                    />
                    {errors.date_of_loss && (
                      <ErrorMessage>{errors.date_of_loss.message}</ErrorMessage>
                    )}
                  </>
                )}
              />
            </Field>
          </div>

          {/* SSN */}
          <Field>
            <Label htmlFor="ssn">Social Security Number</Label>
            <Controller
              name="ssn"
              control={control}
              render={({ field: { onChange, ...field } }) => (
                <>
                  <Input
                    {...field}
                    id="ssn"
                    type="text"
                    placeholder="123-45-6789"
                    onChange={(e) => onChange(formatSSN(e.target.value))}
                    maxLength={11}
                    invalid={!!errors.ssn}
                  />
                  {errors.ssn && (
                    <ErrorMessage>{errors.ssn.message}</ErrorMessage>
                  )}
                </>
              )}
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            {/* Gender */}
            <Field>
              <Label htmlFor="gender">Gender</Label>
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <>
                    <Select {...field} id="gender" invalid={!!errors.gender}>
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </Select>
                    {errors.gender && (
                      <ErrorMessage>{errors.gender.message}</ErrorMessage>
                    )}
                  </>
                )}
              />
            </Field>

            {/* Zip Code */}
            <Field>
              <Label htmlFor="postcode">Zip Code</Label>
              <Controller
                name="address.postcode"
                control={control}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      id="postcode"
                      type="text"
                      placeholder="12345"
                      invalid={!!errors.address?.postcode}
                    />
                    {errors.address?.postcode && (
                      <ErrorMessage>
                        {errors.address.postcode.message}
                      </ErrorMessage>
                    )}
                  </>
                )}
              />
            </Field>
          </div>
        </FieldGroup>
      </Fieldset>

      <div className="mt-6 flex gap-2 justify-end">
        <Button type="button" outline onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={!isValid || isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </form>
  );
};

const useGetOrCreateChannel = ({
  request,
  provider,
}: {
  request: TreatmentRecordRequestSchema;
  provider: MedicalProvider;
}) => {
  const { matterId } = useMatter();

  const [createChannel, { isLoading: isCreating }] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsCreateMutation();
  const [updateChannel, { isLoading: isUpdating }] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsUpdateMutation();

  const getOrCreateChannel = async ({
    schema,
  }: {
    schema: CreateTreatmentRecordRequestChannelSchema;
  }) => {
    if (!request.channel) {
      await createChannel({
        matterId,
        providerId: provider.id,
        requestId: request.id,
        createTreatmentRecordRequestChannelSchema: schema,
      }).unwrap();
    } else {
      await updateChannel({
        matterId,
        providerId: provider.id,
        requestId: request.id,
        channelId: request.channel.id,
        updateTreatmentRecordRequestChannelSchema: {
          ...schema,
        },
      });
    }
  };

  return { getOrCreateChannel, isLoading: isCreating || isUpdating };
};

// Component for handling provider contact information
const ProviderContactModal = ({
  onClose,
  provider,
  request,
}: {
  onClose: () => void;
  provider: MedicalProvider;
  request: TreatmentRecordRequestSchema;
}) => {
  const [channelSchema, setChannelSchema] =
    useState<CreateTreatmentRecordRequestChannelSchema>({
      type: 'fax',
      phone_number: request.channel?.phone_number || undefined,
      fax: request.channel?.fax || undefined,
      email: request.channel?.email || undefined,
      address: request.channel?.address || undefined,
      instructions: request.channel?.instructions || undefined,
    });

  const { getOrCreateChannel, isLoading: isGettingOrCreateChannel } =
    useGetOrCreateChannel({ request, provider });

  const setValue = (
    key: keyof CreateTreatmentRecordRequestChannelSchema,
    value: string,
  ) => {
    setChannelSchema({
      ...channelSchema,
      [key]: value,
    });
  };

  const handleSubmit = async () => {
    try {
      await getOrCreateChannel({
        schema: channelSchema,
      });

      toast.success('Provider contact information updated');
      onClose();
    } catch (error) {
      toast.error('Failed to update provider information');
    }
  };

  const canSubmit =
    channelSchema.phone_number ||
    channelSchema.fax ||
    channelSchema.email ||
    channelSchema.address;

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="phone">Phone Number</Label>
        <Input
          id="phone"
          type="tel"
          value={channelSchema.phone_number || ''}
          onChange={(e) => setValue('phone_number', e.target.value)}
          placeholder="(555) 123-4567"
        />
      </div>

      <DialogActions>
        <Button
          color="zinc"
          onClick={onClose}
          disabled={isGettingOrCreateChannel}
        >
          Cancel
        </Button>
        <Button
          color="indigo-steel"
          onClick={handleSubmit}
          disabled={isGettingOrCreateChannel || !canSubmit}
        >
          {isGettingOrCreateChannel ? 'Saving...' : 'Save Contact Info'}
        </Button>
      </DialogActions>
    </div>
  );
};

// Component for handling portal request ID
const PortalRequestIdModal = ({
  onClose,
  request,
  provider,
}: {
  onClose: () => void;
  request: TreatmentRecordRequestSchema;
  provider: MedicalProvider;
}) => {
  const [portalRequestId, setPortalRequestId] = useState(
    request.channel?.portal_record_request_id || '',
  );

  const { getOrCreateChannel, isLoading: isGettingOrCreateChannel } =
    useGetOrCreateChannel({
      request,
      provider,
    });

  const handleSubmit = async () => {
    if (!portalRequestId.trim()) {
      toast.error('Please enter a portal request ID');
      return;
    }

    try {
      await getOrCreateChannel({
        schema: {
          type: 'portal' as const,
          portal_record_request_id: portalRequestId,
        },
      });
      toast.success('Portal request channel created');
    } catch (error) {
      toast.error('Failed to update portal request ID');
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="portal-id">Portal Request ID</Label>
        <Input
          id="portal-id"
          type="text"
          value={portalRequestId}
          onChange={(e) => setPortalRequestId(e.target.value)}
          placeholder="Enter the portal request ID"
          className="mt-1"
        />
        <p className="text-xs text-gray-500 mt-2">
          This is the tracking ID or reference number from the provider&apos;s
          records portal.
        </p>
      </div>

      <DialogActions>
        <Button
          color="zinc"
          onClick={onClose}
          disabled={isGettingOrCreateChannel}
        >
          Cancel
        </Button>
        <Button
          color="indigo-steel"
          onClick={handleSubmit}
          disabled={isGettingOrCreateChannel || !portalRequestId.trim()}
        >
          {isGettingOrCreateChannel ? 'Saving...' : 'Save Portal ID'}
        </Button>
      </DialogActions>
    </div>
  );
};

// Component for displaying list of blockers
const BlockersList = ({
  blockers,
  onSelectBlocker,
}: {
  blockers: Blockers[];
  onSelectBlocker: (blocker: Blockers) => void;
}) => {
  const getBlockerInfo = (blocker: Blockers) => {
    switch (blocker) {
      case 'CLIENT_INFORMATION_NOT_ENTERED':
        return {
          title: 'Client Information',
          description: 'Required client information is missing',
          icon: '👤',
          action: 'Add Information',
        };
      case 'HIPAA_PACKAGE_NOT_READY':
        return {
          title: 'HIPAA Package',
          description: 'HIPAA authorization package needs to be configured',
          icon: '📋',
          action: 'Configure Package',
        };
      case 'PROVIDER_CONTACT_INFORMATION_NOT_ENTERED':
        return {
          title: 'Provider Contact Information',
          description: 'Provider contact information needs to be entered',
          icon: '🏥',
          action: 'Add Contact',
        };
      case 'PORTAL_REQUEST_ID_REQUIRED':
        return {
          title: 'Portal Request ID',
          description: 'Portal request ID is required',
          icon: '🔑',
          action: 'Add ID',
        };
      case 'VOICE_AI_INFORMATION_NOT_ENTERED':
        return {
          title: 'Voice AI Information',
          description: 'Voice AI information is required',
          icon: '🔊',
          action: 'Add Information',
        };
      default:
        return {
          title: blocker,
          description: 'This item needs to be resolved',
          icon: '⚠️',
          action: 'Resolve',
        };
    }
  };

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">Actions Required</h3>
      <p className="text-xs text-gray-500">
        The following items need to be resolved before the request can proceed:
      </p>
      <div className="space-y-2">
        {blockers.map((blocker) => {
          const info = getBlockerInfo(blocker);
          return (
            <button
              key={blocker}
              onClick={() => onSelectBlocker(blocker)}
              type="button"
              className="w-full p-3 border rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl mt-0.5">{info.icon}</span>
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-900">
                    {info.title}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {info.description}
                  </div>
                </div>
                <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                  {info.action}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const BlockersModal = ({
  blockers,
  request,
  provider,
  isOpen,
  setIsOpen,
  highZIndex = false,
}: {
  blockers: Blockers[];
  request: TreatmentRecordRequestSchema;
  provider?: MedicalProvider;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  highZIndex?: boolean;
}) => {
  const { matterId } = useMatter();
  const { data: matter } = useGetMatterQuery(matterId);
  const [activeModal, setActiveModal] = useState<Blockers | null>(null);

  // Get the first client ID from the matter if available
  const clientId = matter?.clients?.[0]?.id;

  const handleSelectBlocker = (blocker: Blockers) => {
    setActiveModal(blocker);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
    // Show blockers list again after closing a modal
    if (blockers.length > 0) {
      setIsOpen(true);
    }
  };

  const modalZIndex = highZIndex ? Z_INDEX_MODAL_HIGH : undefined;

  return (
    <>
      {/* Main Blockers List Modal */}
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        size="xl"
        zIndex={modalZIndex}
      >
        <DialogBody>
          <BlockersList
            blockers={blockers}
            onSelectBlocker={handleSelectBlocker}
          />
        </DialogBody>
      </Dialog>

      {/* Client Information Modal */}
      <Dialog
        open={activeModal === 'CLIENT_INFORMATION_NOT_ENTERED'}
        onClose={handleCloseModal}
        size="3xl"
        zIndex={modalZIndex}
      >
        <DialogBody>
          <DialogTitle>Client Information Required</DialogTitle>
          <DialogDescription>
            Please provide the required client information to continue
            processing this request.
          </DialogDescription>
          <div className="mt-6">
            <ClientInfoModal
              onClose={handleCloseModal}
              matterId={matterId}
              clientId={clientId}
            />
          </div>
        </DialogBody>
      </Dialog>

      {/* HIPAA Package Modal */}
      {/* <Dialog
        open={activeModal === 'HIPAA_PACKAGE_NOT_READY'}
        onClose={handleCloseModal}
        className="max-w-full!"
      >
        <DialogBody className="w-full h-[85vh] overflow-y-auto">
          <DialogTitle>Configure HIPAA Package</DialogTitle>
          <DialogDescription>
            Configure and generate the HIPAA package for this records request.
          </DialogDescription>
          <div className="mt-4">
            <HipaaPackageWorkflow
              request={request}
              onClose={handleCloseModal}
            />
          </div>
        </DialogBody>
      </Dialog> */}

      {/* Provider Contact Information Modal */}
      <Dialog
        open={activeModal === 'PROVIDER_CONTACT_INFORMATION_NOT_ENTERED'}
        onClose={handleCloseModal}
        size="xl"
        zIndex={modalZIndex}
      >
        <DialogBody>
          <DialogTitle>Provider Contact Information Required</DialogTitle>
          <DialogDescription>
            Please provide contact information for the medical provider to
            process this records request.
          </DialogDescription>
          <div className="mt-6">
            {provider ? (
              <ProviderContactModal
                onClose={handleCloseModal}
                provider={provider}
                request={request}
              />
            ) : (
              <p className="text-sm text-gray-500">
                Provider information not available. Please contact support.
              </p>
            )}
          </div>
        </DialogBody>
      </Dialog>

      {/* Portal Request ID Modal */}
      <Dialog
        open={activeModal === 'PORTAL_REQUEST_ID_REQUIRED'}
        onClose={handleCloseModal}
        size="lg"
        zIndex={modalZIndex}
      >
        <DialogBody>
          <DialogTitle>Portal Request ID Required</DialogTitle>
          <DialogDescription>
            Enter the tracking ID or reference number from the provider&apos;s
            records portal.
          </DialogDescription>
          <div className="mt-6">
            {provider ? (
              <PortalRequestIdModal
                onClose={handleCloseModal}
                request={request}
                provider={provider}
              />
            ) : (
              <p className="text-sm text-gray-500">
                Provider information not available. Please contact support.
              </p>
            )}
          </div>
        </DialogBody>
      </Dialog>

      {/* Voice AI Information Modal */}
      <Dialog
        open={activeModal === 'VOICE_AI_INFORMATION_NOT_ENTERED'}
        onClose={handleCloseModal}
        size="xl"
        zIndex={modalZIndex}
      >
        <DialogBody>
          <DialogTitle>Voice AI Information Required</DialogTitle>
          <DialogDescription>
            Please provide the required voice AI information to continue
            processing this request.
          </DialogDescription>
          <div className="mt-6">
            <VoiceAICallModal
              onClose={handleCloseModal}
              request_id={request.id}
              provider_id={request.provider_id}
            />
          </div>
        </DialogBody>
      </Dialog>
    </>
  );
};

export const ResolveBlockersButton = ({
  request,
  blockedState,
  provider,
  onOverride,
  isDisabled,
  compact = false,
  highZIndex = false,
}: {
  request: TreatmentRecordRequestSchema;
  blockedState: BlockedState;
  provider?: MedicalProvider;
  onOverride: () => void;
  isDisabled: boolean;
  /** Use compact styling for the new RequestRow UI */
  compact?: boolean;
  /** Use higher z-index for modal when inside task panel */
  highZIndex?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const blockers = blockedState.blocked_by;

  if (!blockers || blockers.length === 0) {
    return null;
  }

  // Compact styling for RequestDetail in TreatmentProvidersSection
  if (compact) {
    return (
      <>
        <div className="flex items-center gap-0 min-w-0">
          <ShadcnButton
            size="sm"
            onClick={() => setIsOpen(true)}
            disabled={isDisabled}
            className="h-7 text-xs gap-1 bg-zinc-900 hover:bg-zinc-800 text-white rounded-r-none min-w-0 max-w-full flex-1"
          >
            <span className="truncate">Resolve Blockers</span>
          </ShadcnButton>
          <ShadcnButton
            size="sm"
            onClick={onOverride}
            disabled={isDisabled}
            className="h-7 w-7 p-0 bg-zinc-900 hover:bg-zinc-800 text-white rounded-l-none border-l border-zinc-700"
          >
            <Check className="h-3.5 w-3.5" />
          </ShadcnButton>
        </div>
        <BlockersModal
          blockers={blockers}
          request={request}
          provider={provider}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          highZIndex={highZIndex}
        />
      </>
    );
  }

  // Default styling for pizza tracker
  return (
    <>
      <Button
        color="indigo-steel"
        className="rounded-r-none!"
        onClick={() => setIsOpen(true)}
        disabled={isDisabled}
      >
        Resolve Blockers
      </Button>
      <Button
        color="indigo-steel"
        onClick={onOverride}
        disabled={isDisabled}
        className="rounded-l-none!"
      >
        <CheckCircleIcon className="w-4 h-4" />
      </Button>
      <BlockersModal
        blockers={blockers}
        request={request}
        provider={provider}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        highZIndex={highZIndex}
      />
    </>
  );
};
